import React, { useEffect } from 'react';
import { X, ExternalLink, Radio } from 'lucide-react';
import { PerformanceRecording } from '../types';

interface VideoModalProps {
  recording: PerformanceRecording | null;
  onClose: () => void;
}

export const VideoModal: React.FC<VideoModalProps> = ({ recording, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (recording) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [recording, onClose]);

  if (!recording) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">{recording.title}</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {recording.composer} &bull; {recording.instrumentation}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="modal-close-btn"
            title="Close video modal"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <iframe
          className="modal-video-frame"
          src={`https://www.youtube-nocookie.com/embed/${recording.youtube_id}?autoplay=1&rel=0`}
          title={recording.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />

        {recording.streaming_links && Object.keys(recording.streaming_links).length > 0 && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Listen on preferred platform:
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {Object.entries(recording.streaming_links).map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  style={{ padding: '0.35rem 0.75rem', fontSize: '0.78rem', gap: '0.35rem' }}
                >
                  <Radio size={12} color="var(--accent-gold)" />
                  <span>{platform}</span>
                  <ExternalLink size={10} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
